import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { graphql, useStaticQuery } from 'gatsby';
import { Container, Icon, List } from '@pella/sash-framework';
import { CTA } from '../../';

import MegaMenu from './mega-menu';
import StandardMenu from './standard-menu';
import MobileMenu from '../mobile-menu';

const Header = ({ className, children, ...other }) => {
	const pageData = useStaticQuery(query).contentstackCwsMenu;
	const [isMobileActive, setIsMobileActive] = useState(false);
	const [isActiveShadow, setIsActiveShadow] = useState(false);

	const onMenuItemEnter = () => setIsActiveShadow(true);
	const onMenuItemLeave = () => setIsActiveShadow(false);

	const onMobileTriggerClick = () => {
		setIsMobileActive(!isMobileActive);
		setIsActiveShadow(!isMobileActive);

		if (document.body.classList.contains('overflow-hidden')) {
			document.body.classList.remove('overflow-hidden');
		} else {
			document.body.classList.add('overflow-hidden');
		}
	};

	return (
		<>
			<header
				id='header'
				className={`header relative z-[20] border-b border-b-gray-5 ${className || ''}`}
				{...other}
			>
				{pageData.global?.cta?.[0] && (
					<div className='header-global hidden bg-gray-3 py-1.5 lg:block'>
						<Container>
							<List
								bullets='none'
								layout='inline'
								className='items-center justify-end divide-x divide-secondary-light'
							>
								{pageData.global.cta.map((cta, index) => (
									<li
										key={index}
										className={`pr-2 leading-none ${index > 0 ? 'ml-2 pl-4' : 'pl-2'}`}
									>
										<CTA
											data={cta}
											className='group inline-flex flex-row-reverse items-center border-b-2 border-b-transparent no-underline hover:border-b-primary focus:border-b-primary [&>span]:ml-1 [&>span]:!text-xs'
										/>
									</li>
								))}
							</List>
						</Container>
					</div>
				)}

				{pageData.menu?.[0] && (
					<nav className='header-nav bg-white py-3 xl:py-4'>
						<Container>
							<List
								bullets='none'
								layout='inline'
								className='items-center lg:justify-start'
							>
								{pageData.home?.[0] && (
									<li className='header-logo xl:mr-1'>
										<CTA
											data={{
												...pageData.home[0],
												file: { url: pageData.home[0].file.url + '?format=png&auto=webp&height=43' },
											}}
											className='[&_img]:h-8 md:[&_img]:h-11'
										/>
									</li>
								)}

								{pageData.menu.map((item, index) => (
									<li
										key={index}
										className='header-navItem group relative hidden lg:block lg:pl-2'
										onMouseEnter={onMenuItemEnter}
										onMouseLeave={onMenuItemLeave}
										onFocus={onMenuItemEnter}
										onBlur={onMenuItemLeave}
									>
										{item.direct_url && (
											<CTA
												data={{
													destination: [{ destination: item.direct_url }],
													cta_title: item.heading,
												}}
												className='rounded px-2 py-3 text-base font-semibold no-underline transition-colors hover:bg-[rgba(0,130,62,0.1)] focus:bg-[rgba(0,130,62,0.1)] active:bg-primary active:text-white'
											/>
										)}

										{!item.direct_url && <button className='px-2 py-3 text-base font-semibold before:absolute before:-bottom-px before:left-1/2 before:z-[20] before:-translate-x-1/2 before:border-8 before:border-transparent before:border-b-white before:content-none after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:border-[9px] after:border-transparent after:border-b-gray-10 after:content-none group-focus-within:before:content-[""] group-focus-within:after:content-[""] group-hover:before:content-[""] group-hover:after:content-[""]'>{item.heading}</button>}

										{item.sortable?.[0] && <MegaMenu data={item} />}
										{item.standard?.[0] && <StandardMenu data={item} />}
									</li>
								))}

								{pageData?.global?.cta?.find((cta) => cta.cta_title === 'Portal Login') && (
									<li className='header-navItem-portal relative ml-auto hidden md:mr-6 md:block md:pr-6 md:before:absolute md:before:right-0 md:before:top-1/2 md:before:h-4 md:before:w-px md:before:-translate-y-1/2 md:before:bg-secondary-light lg:hidden'>
										<CTA
											data={pageData.global.cta.find((cta) => cta.cta_title === 'Portal Login')}
											className='inline-flex flex-row-reverse items-center font-semibold no-underline [&>span]:ml-1 [&>span]:!text-base'
										/>
									</li>
								)}

								<li className='header-navItem-mobileTrigger ml-auto md:ml-0 lg:hidden'>
									<button
										onClick={onMobileTriggerClick}
										className='header-mobileBtn flex items-center !leading-none'
										aria-expanded={isMobileActive}
										aria-label='Mobile Menu'
									>
										<span className='hidden text-base font-semibold md:inline-block'>Menu</span>
										<Icon
											name='menu'
											size={24}
											className='ml-2 block'
										/>
									</button>
								</li>
							</List>
						</Container>
					</nav>
				)}
			</header>

			{(isMobileActive || isActiveShadow) && <div className='fauxShadow fixed left-0 top-0 z-[18] h-full w-full bg-black/30 backdrop-blur-sm' />}
			{isMobileActive && <MobileMenu data={pageData} />}
		</>
	);
};

Header.propTypes = {
	className: PropTypes.string,
	children: PropTypes.node,
};

export default Header;

export const query = graphql`
	{
		contentstackCwsMenu {
			home {
				...CTA
			}
			global {
				cta {
					...CTA
				}
			}
			menu {
				heading
				direct_url
				sortable {
					heading
					by_type {
						heading
						ctas {
							cta {
								...CTA
							}
							flag_hex_value
							flag
							material_symbol
							text_hex_value
						}
					}
					cta {
						...CTA
					}
				}
				standard {
					heading
					cta {
						...CTA
					}
				}
				cta {
					...CTA
				}
			}
		}
	}
`;
